import {
	hasMinOSVer,
	isAppleDevice,
	presentDeviceIds$,
	wasPresenceChecked$,
	type DeviceBlockStatus,
	type ManagedDevice,
} from '@op/services'
import { createSelector } from '@reduxjs/toolkit'
import type {
	DeviceInfoChildAppStatus,
	DeviceInfoOnlineStatus,
	DeviceInfoPairStatus,
	ScreenTimeViewData,
} from '../types/DeviceInfoViewData.js'

const deviceParamSelector = (_: unknown, device: ManagedDevice) => device
const statusParamSelector = (_: unknown, status: DeviceBlockStatus) => status
const statusAndDeviceSelector = (_: unknown, _device: ManagedDevice, status: DeviceBlockStatus) =>
	status

export const getDevicePairStatus = (device: ManagedDevice): DeviceInfoPairStatus => {
	if (device.management_removed) return 'unknown'
	if (!isAppleDevice(device)) return 'full_pair'
	return device.management_level == 'ios-supervised' ? 'full_pair' : 'quick_pair'
}

/**
 * Returns the battery level of a device as a percentage. Returns null if the
 * device doesn't have a valid battery level.
 */
export const deviceBattery$ = createSelector(deviceParamSelector, (device): number | null => {
	const level = device.attributes?.battery_level
	if (level == null) return null
	const pct = Math.round(level * 100)
	return Number.isFinite(pct) ? pct : null // Guard against NaN
})

/**
 * Returns the online status of a device. This requires presence data,
 * and the device to be paired.
 */
export const deviceOnlineStatus$ = createSelector(
	deviceParamSelector,
	presentDeviceIds$,
	wasPresenceChecked$,
	(device, presentDeviceIds, wasPresenceChecked): DeviceInfoOnlineStatus => {
		const isAppInstalled = Boolean(device.app && device.app_version && device.system_components)
		if (!wasPresenceChecked || !isAppInstalled) return 'unknown'
		return presentDeviceIds.has(device.device_id) ? 'online' : 'offline'
	},
)

/**
 * Determines if a device is tamperproofed. Returns null if device doesn't
 * currently support tamperproofing.
 */
export const deviceTamperproofed$ = createSelector(
	deviceParamSelector,
	(device): boolean | null => {
		const isIOSUnsupported = device.os === 'iOS' && hasMinOSVer(26, device)
		if (isIOSUnsupported) return null
		return device.management_level === 'ios-supervised' || device.os === 'android'
	},
)

/**
 * Returns the pairing status of a device. If the device is unmanaged, returns 'unknown'.
 * Android devices are always 'full_pair'.
 * iOS devices are 'full_pair' if they are supervised, and 'quick_pair' if they are not.
 */
export const devicePairStatus$ = createSelector(deviceParamSelector, getDevicePairStatus)

/**
 * Returns the timestamp of when a device was paired. If the device is not managed, returns the
 * timestamp of when the device was last active.
 *
 * This one is a bit confusing, but basically, if the device is not managed (unpaired), we want
 * to show when we saw the last activity.
 */
export const devicePairTs$ = createSelector(
	deviceParamSelector,
	statusAndDeviceSelector,
	(device, status): number => {
		const isManaged = status.viewState !== 'unmanaged'
		return isManaged ? device.created : (device.last_activity ?? device.created)
	},
)

/**
 * Returns the `DeviceChildAppStatus` for a given `ManagedDevice` and `ViewBlockState`.
 */
export const deviceChildAppStatus$ = createSelector(
	deviceParamSelector,
	statusAndDeviceSelector,
	devicePairStatus$,
	(device, _status, pairStatus): DeviceInfoChildAppStatus => {
		const isAppInstalled = Boolean(device.app && device.app_version && device.system_components)
		return isAppInstalled && pairStatus != null ? 'installed' : 'unknown'
	},
)

/**
 * Returns the `ScreenTimeViewData` object for a given `DeviceBlockStatus`.
 */
export const deviceScreenTimeViewData$ = createSelector(
	statusParamSelector,
	(status): ScreenTimeViewData => {
		const isManaged = status.viewState != 'unmanaged'
		if (!isManaged) return { colorState: 'unmanaged', display: 'unmanaged' }

		const statusKey =
			status.viewState == 'paused' ? 'paused'
			: status.status == 'blocked' ? 'blocked'
			: 'granted'

		return { colorState: status.viewState, display: 'status', statusKey }
	},
)
